import { useEffect, useState } from "react";
import { Loader2, LogOut, Plus, Star, Pencil, Trash2, ExternalLink, KeyRound, ImagePlus } from "lucide-react";
import { api, formatError, imgSrc, eur, fmtDate } from "@/lib/api";
import PropertyForm from "@/pages/admin/PropertyForm";
import PhotoManager from "@/pages/admin/PhotoManager";
import PasswordModal from "@/pages/admin/PasswordModal";
import LeadsTab from "@/pages/admin/LeadsTab";
import ProfilesTab from "@/pages/admin/ProfilesTab";
import VisitsTab from "@/pages/admin/VisitsTab";

const inputCls =
  "w-full rounded-lg border border-gold/20 bg-ink px-4 py-3 text-ivory placeholder:text-stone2/60 outline-none transition-colors focus:border-gold";

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data.user);
    } catch (error) {
      setErr(formatError(error.response?.data?.detail) || "Credenciales incorrectas.");
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-2xl border border-gold/25 bg-ink2/90 p-8 shadow-[0_10px_30px_rgba(0,0,0,0.8)] md:p-10"
      >
        <div className="text-center">
          <img src="/logo.jpg" alt="Inmobiliaria Tecnohogar" className="mx-auto h-20 w-auto" />
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.3em] text-gold-subtle">Panel de gestión</p>
        </div>
        <div className="mt-8 space-y-4">
          <input
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            className={inputCls}
          />
          <input
            type="password"
            required
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className={inputCls}
          />
        </div>
        {err && (
          <p className="mt-4 text-center text-sm text-red-400" data-testid="admin-login-error">
            {err}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="btn-gold-metallic mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold uppercase tracking-[0.2em] text-ink disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Entrar"}
        </button>
      </form>
    </div>
  );
};

const PropertiesTab = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined = list, null = new, object = edit
  const [photoProp, setPhotoProp] = useState(null);

  const load = () =>
    api
      .get("/admin/properties")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const toggleFeatured = async (p) => {
    await api.put(`/properties/${p.id}`, { ...p, featured: !p.featured });
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"? Esta acción no se puede deshacer.`)) return;
    await api.delete(`/properties/${p.id}`);
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-ivory">Propiedades</h2>
        <button
          onClick={() => setEditing(null)}
          data-testid="admin-new-property-button"
          className="btn-gold-metallic flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-ink"
        >
          <Plus size={15} /> Nueva propiedad
        </button>
      </div>

      {editing !== undefined && (
        <div className="mt-6">
          <PropertyForm initial={editing} onSaved={() => { setEditing(undefined); load(); }} onCancel={() => setEditing(undefined)} />
        </div>
      )}

      {items === null && (
        <div className="mt-10 flex justify-center">
          <Loader2 className="animate-spin text-gold" size={28} />
        </div>
      )}

      {items && items.length === 0 && (
        <p className="mt-10 rounded-xl border border-gold/15 bg-ink2/60 p-8 text-center text-stone2" data-testid="admin-properties-empty">
          Aún no hay propiedades. Pulsa «Nueva propiedad» para crear la primera.
        </p>
      )}

      <div className="mt-6 space-y-4">
        {items &&
          items.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-property-row-${p.id}`}
              className="flex flex-wrap items-center gap-4 rounded-xl border border-gold/15 bg-ink2/70 p-4"
            >
              {p.images && p.images[0] ? (
                <img src={imgSrc(p.images[0])} alt="" className="h-16 w-24 rounded-lg object-cover" />
              ) : (
                <div className="h-16 w-24 rounded-lg bg-ink" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ivory">{p.title}</p>
                <p className="mt-0.5 text-xs text-stone2">
                  <span className="uppercase tracking-widest text-gold-subtle">{p.operation}</span> · {eur(p.price)}
                  {p.operation === "alquiler" && "/mes"} · {p.location} · Ref. {p.ref}
                </p>
              </div>
              <button
                onClick={() => setPhotoProp(p)}
                data-testid={`admin-photos-${p.id}`}
                title="Añadir o quitar fotos"
                className="flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2.5 text-xs font-semibold text-gold transition-colors hover:bg-gold/10"
              >
                <ImagePlus size={16} /> Fotos{p.images ? ` (${p.images.length})` : ""}
              </button>
              <button
                onClick={() => toggleFeatured(p)}
                data-testid={`admin-toggle-featured-${p.id}`}
                title="Destacada"
                className={`rounded-full border p-2.5 transition-colors ${p.featured ? "border-gold text-gold" : "border-gold/25 text-stone2 hover:text-gold"}`}
              >
                <Star size={16} fill={p.featured ? "currentColor" : "none"} />
              </button>
              <button
                onClick={() => setEditing(p)}
                data-testid={`admin-edit-property-${p.id}`}
                title="Editar"
                className="rounded-full border border-gold/25 p-2.5 text-stone2 transition-colors hover:text-gold"
              >
                <Pencil size={16} />
              </button>
              <button
                onClick={() => remove(p)}
                data-testid={`admin-delete-property-${p.id}`}
                title="Eliminar"
                className="rounded-full border border-red-500/30 p-2.5 text-red-400 transition-colors hover:bg-red-500/10"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
      </div>

      {photoProp && (
        <PhotoManager
          property={photoProp}
          onSaved={() => { setPhotoProp(null); load(); }}
          onClose={() => setPhotoProp(null)}
        />
      )}
    </div>
  );
};

const Admin = () => {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("propiedades");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (user === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );

  if (!user) return <LoginCard onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-ink text-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-gold/20 bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Tecnohogar" className="h-10 w-auto" />
            <span className="hidden text-xs uppercase tracking-[0.25em] text-gold-subtle sm:block">Panel de gestión</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPassword(true)}
              data-testid="admin-change-password-button"
              title="Cambiar mi clave"
              className="flex items-center gap-1.5 rounded-full border border-gold/30 px-4 py-2 text-sm text-gold transition-colors hover:bg-gold/10"
            >
              <KeyRound size={15} /> <span className="hidden sm:inline">Cambiar clave</span>
            </button>
            <a href="/" data-testid="admin-view-site-link" className="flex items-center gap-1.5 text-sm text-stone2 hover:text-gold">
              <ExternalLink size={15} /> Ver web
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 rounded-full border border-gold/30 px-4 py-2 text-sm text-gold transition-colors hover:bg-gold/10"
            >
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8 md:px-8">
        <div className="flex gap-2">
          <button
            onClick={() => setTab("propiedades")}
            data-testid="admin-tab-properties"
            className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] transition-all ${
              tab === "propiedades" ? "btn-gold-metallic text-ink" : "border border-gold/25 text-gold hover:bg-gold/10"
            }`}
          >
            Propiedades
          </button>
          <button
            onClick={() => setTab("mensajes")}
            data-testid="admin-tab-leads"
            className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] transition-all ${
              tab === "mensajes" ? "btn-gold-metallic text-ink" : "border border-gold/25 text-gold hover:bg-gold/10"
            }`}
          >
            Mensajes de clientes
          </button>
          <button
            onClick={() => setTab("fichas")}
            data-testid="admin-tab-profiles"
            className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] transition-all ${
              tab === "fichas" ? "btn-gold-metallic text-ink" : "border border-gold/25 text-gold hover:bg-gold/10"
            }`}
          >
            Fichas de clientes
          </button>
          <button
            onClick={() => setTab("reservas")}
            data-testid="admin-tab-bookings"
            className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] transition-all ${
              tab === "reservas" ? "btn-gold-metallic text-ink" : "border border-gold/25 text-gold hover:bg-gold/10"
            }`}
          >
            Reservas de visitas
          </button>
        </div>
        <div className="mt-8">
          {tab === "propiedades" ? (
            <PropertiesTab />
          ) : tab === "mensajes" ? (
            <LeadsTab />
          ) : tab === "fichas" ? (
            <ProfilesTab />
          ) : (
            <VisitsTab />
          )}
        </div>
        <p className="mt-12 text-center text-xs text-stone2/60">
          Sesión iniciada como {user.email} · {fmtDate(new Date().toISOString())}
        </p>
      </div>

      {showPassword && <PasswordModal onClose={() => setShowPassword(false)} />}
    </div>
  );
};

export default Admin;
